import { CloseOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  DatePicker,
  DeleteButton,
  EditButton,
  Form,
  getDefaultSortOrder,
  Icons,
  Input,
  List,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  TextField,
  useTable,
} from "@pankod/refine-antd";
import {
  CrudFilters,
  HttpError,
  IResourceComponentsProps,
  useUpdate,
} from "@pankod/refine-core";
import dayjs from "dayjs";
import { ShootExecutionStatus } from "enums";
import { ICampaignShoot } from "interfaces";
import { FC, useState } from "react";
import { shootExecutionStatusMap } from "utils/constants";

interface IShootExecutionFilters {
  campaign_name: string;
  scheduled_from: string;
  status: string;
}

export const ShootExecutionList: FC<IResourceComponentsProps> = () => {
  const [enabled, setEnabled] = useState(false);

  const { tableProps, sorter, searchFormProps, tableQueryResult } = useTable<
    ICampaignShoot,
    HttpError,
    IShootExecutionFilters
  >({
    initialCurrent: 1,
    initialPageSize: 10,
    hasPagination: enabled,
    onSearch: (values) => {
      const filtros: CrudFilters = [
        { field: "campaign_name", operator: "eq", value: values.campaign_name },
        {
          field: "scheduled_from",
          operator: "eq",
          value: values.scheduled_from
            ? dayjs(values.scheduled_from).startOf("day").toISOString()
            : null,
        },
        {
          field: "scheduled_to",
          operator: "eq",
          value: values.scheduled_from
            ? dayjs(values.scheduled_from).endOf("day").toISOString()
            : null,
        },
        { field: "status", operator: "eq", value: values.status },
      ];

      const hasFilters = filtros.some((f) => f.value);
      setEnabled(hasFilters);

      return filtros;
    },
    queryOptions: { enabled },
  });

  const { mutate: updateExecution } = useUpdate({});

  const handleCleanFilters = () => {
    searchFormProps.form?.resetFields();
    setEnabled(false);
  };

  const handleCancel = (id: string) => {
    updateExecution(
      {
        metaData: { method: "PATCH" },
        resource: "/v1/campaign-executions",
        id,
        values: { status: ShootExecutionStatus.CANCELLED },
        successNotification: {
          type: "success",
          message: "Item cancelado com sucesso",
        },
      },
      {
        onSuccess: () => {
          tableQueryResult.refetch();
        },
      }
    );
  };

  return (
    <List
      title="Executar Disparo"
      createButtonProps={{ children: "Novo Disparo", type: "primary" }}
    >
      <Card title="Filtrar">
        <Form {...searchFormProps} layout="vertical" style={{ padding: 16 }}>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="campaign_name" label="Campanha">
                <Input placeholder="Campanha" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item name="scheduled_from" label="Data/Hora de Disparo">
                <DatePicker placeholder="00/00/0000" format="DD/MM/YYYY" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item name="status" label="Status">
                <Select placeholder="Status" allowClear>
                  {Object.entries(ShootExecutionStatus).map(([key, value]) => {
                    const { Icon: StatusIcon, ...status } =
                      shootExecutionStatusMap[value];

                    return (
                      <Select.Option key={key} value={value}>
                        <Space align="center">
                          <StatusIcon style={{ color: status.color }} />
                          {status.label}
                        </Space>
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} style={{ textAlign: "right", marginTop: 8 }}>
              <Space>
                <Button
                  type="default"
                  icon={<Icons.ClearOutlined />}
                  onClick={handleCleanFilters}
                >
                  Limpar
                </Button>
                <Button
                  type="primary"
                  icon={<Icons.SearchOutlined />}
                  htmlType="submit"
                >
                  Buscar
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Card>

      <Table
        {...tableProps}
        rowKey="_id"
        dataSource={
          enabled && !tableProps.loading ? tableProps?.dataSource : []
        }
        loading={enabled ? tableProps.loading : false}
        pagination={{
          ...tableProps.pagination,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} de ${total} registros`,
        }}
        expandable={{
          expandedRowRender: (record) => (
            <div style={{ marginLeft: "92px" }}>
              <Table
                dataSource={record.templates_schedule}
                pagination={false}
                columns={[
                  { title: "Nome do Template", dataIndex: "template_name" },
                  {
                    title: "Data/Hora de Disparo",
                    dataIndex: "scheduled_datetime",
                    render: (value) =>
                      value ? dayjs(value).format("DD/MM/YYYY HH:mm:ss") : "-",
                  },
                  {
                    title: "Status do Disparo",
                    dataIndex: "template_status",
                    render: (value) => {
                      if (!value) return "-";
                      const { Icon: IconStatus, ...status } =
                        shootExecutionStatusMap[value];

                      return (
                        <Space>
                          <IconStatus style={{ color: status.color }} />
                          <TextField
                            style={{ whiteSpace: "nowrap" }}
                            value={status.label}
                          />
                        </Space>
                      );
                    },
                  },
                  { title: "Ordem de Disparo", dataIndex: "order" },
                ]}
                size="small"
              />
            </div>
          ),
        }}
      >
        <Table.Column
          dataIndex="campaign_name"
          key="campaign_name"
          title="Campanha"
          render={(value) => <TextField value={value ?? "-"} />}
        />

        <Table.Column
          dataIndex="status"
          key="status"
          title="Status do Disparo"
          render={(value) => {
            const { Icon: IconStatus, ...status } =
              shootExecutionStatusMap[value];

            return (
              <Space>
                <IconStatus style={{ color: status.color }} />
                <TextField
                  style={{ whiteSpace: "nowrap" }}
                  value={status.label}
                />
              </Space>
            );
          }}
        />

        <Table.Column
          dataIndex="total_leads"
          key="total_leads"
          title="Quantidade de Leads (CSV)"
        />

        <Table.Column
          dataIndex="processed_count"
          key="processed_count"
          title="Quantidade de Disparos"
        />

        <Table.Column
          dataIndex="cancelled_at"
          key="cancelled_at"
          title="Data/Hora Cancelamento"
          render={(value) => (
            <TextField
              value={value ? dayjs(value).format("DD/MM/YYYY HH:mm:ss") : "-"}
            />
          )}
          defaultSortOrder={getDefaultSortOrder("cancelled_at", sorter)}
          sorter
        />

        <Table.Column
          title="Ações"
          render={(_, record: ICampaignShoot) => (
            <Space>
              {record.status === ShootExecutionStatus.DRAFT && (
                <>
                  <EditButton hideText size="small" recordItemId={record._id} />
                  <Popconfirm
                    title="Tem certeza que deseja cancelar este item?"
                    okText="Sim"
                    cancelText="Não"
                    onConfirm={() => handleCancel(record._id)}
                  >
                    <Button danger size="small" icon={<CloseOutlined />} />
                  </Popconfirm>
                  <DeleteButton
                    hidden
                    hideText
                    size="small"
                    recordItemId={record._id}
                  />
                </>
              )}
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
